import classNames from "classnames";
import { useState } from "react";
import { Mode } from "..";
import { YouCard } from "../../../../../components/cards/youCard";
import { MorePopOverMenu } from "../../../../../components/dropdown/moreMenuPopover";
import { Icon } from "../../../../../components/icons/icon";
import { MemberProfilePic } from "../../../../../components/profile/memberPicture";
import { TableComponent } from "../../../../../components/table/table";
import {
  getOffsetFromPage,
  getPageFromOffset,
} from "../../../../../helperFunctions";

import { Button } from "../../../../../components/buttons/button";
import { SearchInput } from "../../../../../components/inputs/searchInput";
import { InviteNewCooperativeMemberModal } from "../../../../../components/modals/app_modals/members/inviteMemberModal";
import { MemberDetailsModal } from "../../../../../components/modals/app_modals/members/memberDetailsModal";
import { RemoveMemberModal } from "../../../../../components/modals/app_modals/members/memberRemovalConfirmModal";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import {
  SingleCooperativeDetails,
  SingleCooperativeMember,
} from "../../../../../redux/features/cooperative/cooperative.type";
import { useGetCooperativeMembersQuery } from "../../../../../redux/features/membership/membership.api";
import { CooperativeMembersQuery } from "../../../../../redux/features/membership/membership.type";
import { PermissionCode } from "../../../../../redux/features/settings/settings.type";
import { useAppSelector } from "../../../../../redux/hooks";

export const CooperativeMembers = ({
  cooperativeDetails,
  mode,
  permissions,
}: {
  cooperativeDetails: SingleCooperativeDetails;
  mode: Mode;
  permissions: PermissionCode[];
}) => {
  const [query, setQuery] = useState<CooperativeMembersQuery>({
    offset: 0,
    limit: 20,
    coop: cooperativeDetails.id,
    text: undefined,
  });
  const [selectedMember, setSelectedMember] =
    useState<SingleCooperativeMember>();

  const { profile } = useAppSelector((state) => state.auth);

  const modalControls = useModalControl();

  const { modal, modalOpen, handleModalClose, handleModalOpen } = modalControls;

  const { data: members, isFetching: fetchingMembers } =
    useGetCooperativeMembersQuery(query, {
      refetchOnMountOrArgChange: true,
    });

  const onRemoveMember = (mem: SingleCooperativeMember) => {
    setSelectedMember(mem);
    handleModalOpen("removeMemberModal");
  };
  return (
    <>
      <div className="mb-4 md:mb-6 flex flex-wrap items-center gap-4 md:gap-2 w-full">
        <p className="text-sm max-md:w-full md:flex-1 font-medium text-pry-dark-green-300/80">
          {members?.data?.count ?? 0}{" "}
          {members?.data?.count === 1 ? "member" : "members"}
        </p>

        <SearchInput
          placeholder="Search member name, email address"
          searchFunction={(val) => {
            setQuery({ ...query, text: val, offset: 0 });
          }}
          className="w-full md:max-w-[286px] !px-4 !py-2 max-md:order-last"
          iconDimension={16}
        />

        {mode === "admin" && permissions.includes("can.add.member") ? (
          <div className="grid grid-cols-1 gap-2 max-md:w-full md:w-fit">
            <Button
              type="button"
              text="Add Members"
              size="small"
              className="!text-xs"
              icon={"add-profile-icon"}
              onClick={() => {
                handleModalOpen("inviteMemberModal");
              }}
            />

            {/* <Button
              type="button"
              text="Upload"
              size="small"
              className="!text-xs"
              variation="plain-2"
              icon={"file-upload-icon"}
              onClick={() => {
                handleModalOpen("adminBulkUploadMembersModal");
              }}
            /> */}
          </div>
        ) : undefined}
      </div>

      <TableComponent
        className="w-full !min-w-max md:!min-w-full"
        isRowClickable={mode === "admin"}
        headers={[]}
        isLoading={fetchingMembers}
        isTableEmpty={[0, undefined].includes(members?.data?.results?.length)}
        currentPage={getPageFromOffset(query.offset ?? 0, query.limit ?? 0)}
        isPaginated
        itemsPerPage={query?.limit}
        onPageChange={(page) => {
          setQuery({
            ...query,
            offset: getOffsetFromPage(page, query.limit ?? 0),
          });
        }}
        emptrySrc="/assets/images/empty_state_member.svg"
        emptyTitle="We couldn’t find any member"
        totalCount={members?.data?.count}
        rowClassName="!py-2"
        loadingHeight={32}
        rows={
          members?.data?.results?.map((member, idx) => {
            const key = `$cooperative-member-${idx}`;

            return {
              id: member?.id,
              onClick: () => {
                setSelectedMember(member);
                handleModalOpen("memberDetailsModal");
              },
              items: [
                <div
                  key={`${key}-member-details`}
                  className="flex items-center gap-2"
                >
                  <MemberProfilePic
                    member={member.User}
                    className="!w-[32px] !h-[32px] !text-xs !font-medium"
                  />

                  <div className="flex flex-col flex-1 gap-1 md:gap-2 md:flex-row md:items-center">
                    <p className="font-medium">
                      {member?.User?.FirstName} {member?.User?.LastName}
                    </p>

                    <p
                      className={classNames(
                        "font-medium text-pry-dark-green-300/50",
                        {
                          "md:order-last md:ml-auto":
                            mode === "personal" ||
                            (mode === "admin" &&
                              !permissions.includes("can.remove.member")),
                        }
                      )}
                    >
                      {member?.User?.Email}
                    </p>

                    {profile?.Email === member?.User?.Email ? (
                      <YouCard />
                    ) : undefined}
                  </div>

                  {mode === "admin" &&
                  permissions.includes("can.remove.member") &&
                  profile?.Email !== member?.User?.Email ? (
                    <>
                      <button
                        type="button"
                        className="max-md:hidden flex items-center gap-2 w-fit"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveMember(member);
                        }}
                      >
                        <Icon
                          svg="remove-profile-icon"
                          width={16}
                          height={16}
                        />
                        Remove Member
                      </button>

                      <MorePopOverMenu
                        options={[
                          {
                            icon: "remove-profile-icon",
                            text: "Remove Member",
                            onClick: () => {
                              onRemoveMember(member);
                            },
                            hidden: false,
                          },
                        ]}
                        className="md:hidden"
                      />
                    </>
                  ) : undefined}
                </div>,
              ],
            };
          }) ?? []
        }
      />

      {selectedMember &&
      mode === "admin" &&
      permissions.includes("can.remove.member") ? (
        <RemoveMemberModal
          isModalOpen={modalOpen}
          onModalClose={() => {
            handleModalClose();
            setSelectedMember(undefined);
          }}
          member={selectedMember}
          modal={modal}
          successCallback={() => {
            handleModalOpen("removeMemberSuccessModal");
          }}
        />
      ) : undefined}

      {selectedMember ? (
        <MemberDetailsModal
          memberId={selectedMember?.id}
          cooperativeId={cooperativeDetails.id}
          modalControls={modalControls}
          onClose={() => {
            setSelectedMember(undefined);
          }}
        />
      ) : undefined}

      <InviteNewCooperativeMemberModal
        isModalOpen={modalOpen}
        modal={modal}
        onModalClose={() => {
          handleModalClose();
        }}
        successCallback={() => {
          handleModalOpen("inviteMemberSuccessModal");
        }}
        singleCooperative={cooperativeDetails}
      />

      {/* <BulkUploadMembersModal
        modalOpen={modalOpen}
        modal={modal}
        handleModalClose={handleModalClose}
        handleModalOpen={handleModalOpen}
        cooperativeDetails={cooperativeDetails}
        onSuccess={() => {}}
      /> */}
    </>
  );
};
